import BaseListPresenter from "../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";
import xhr from "../../usecases/xhr/Xhr";

class ReportPresenter extends BaseListPresenter {
  constructor(view, findObjectUseCase, countObjectUseCase, exportCSVUseCase) {
    super(view, findObjectUseCase, countObjectUseCase, exportCSVUseCase);
    this.exportCSVUseCase = exportCSVUseCase;
  }
  init() {
    this.limit = 50000;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterType = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  reset() {
    this.objects = [];
    this.view.state.aggregateData = {};
    this.count = 0;
    this.current = 1;
    this.view.setObjects([]);
    this.view.setSelected([]);
    this.view.setCount(0);
    this.view.setTotal(0);
  }

  componentDidMount() {
    this.init();
    this.findStore();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async getObjects() {
    this.reset();
    this.find();
    // await this.aggregateData();
  }

  async findStore() {
    const user = await getCurrentUserUseCase().execute();
    const storeQuery = { include: ["all"], where: { email: user.email } };
    this.findObjectUseCase.abort();
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    );
    this.view.setStore(store);
  }

  async find() {
    try {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        console.log("Aborted previous requests");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];
      const startTime = Date.now();

      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      const dateRanges = this.generateDateRanges(startDate, endDate);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];

        const chunkQuery = {
          ...query,
          where: {
            ...query.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        console.log("chunkQuery", chunkQuery);

        try {
          const chunkData = await xhr.get("/transactions", {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          this.objects = this.objects.concat(chunkData);
          this.aggregateData(this.objects);
          this.view.setObjects(this.objects);
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("Request was aborted at chunk", i + 1);
            return;
          }
          console.error(`Error fetching ${range.label}:`, chunkError.message);
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );
      this.abortController = null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Entire operation was aborted");
        return;
      }
      console.error("Error:", error.message);
    }
  }

  cleanup() {
    if (this.abortController) {
      console.log("🧹 Cleaning up...");
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  // Simple range generator: ≤31 days = 1 request, >31 days = monthly chunks
  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    // If 31 days or less, single request
    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    // More than 31 days, use monthly chunks
    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // Generate monthly ranges
  // generateMonthlyRanges(startDate, endDate) {
  //   const ranges = [];
  //   let current = new Date(startDate);

  //   while (current < endDate) {
  //     const monthStart = new Date(current);
  //     const monthEnd = new Date(
  //       current.getFullYear(),
  //       current.getMonth() + 1,
  //       0,
  //       23,
  //       59,
  //       59,
  //     );

  //     if (monthEnd > endDate) {
  //       monthEnd.setTime(endDate.getTime());
  //     }

  //     ranges.push({
  //       start: this.formatDate(monthStart),
  //       end: this.formatDate(monthEnd),
  //       label: monthStart.toLocaleString("default", {
  //         month: "short",
  //         year: "numeric",
  //       }),
  //     });

  //     current.setMonth(current.getMonth() + 1);
  //   }

  //   return ranges;
  // }

  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      // ADD +1 day dito para sa business day
      const businessDayEnd = new Date(monthEnd);
      businessDayEnd.setDate(businessDayEnd.getDate() + 1);

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatDate(businessDayEnd), // Use businessDayEnd
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  // Format date for API
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  // createQuery() {
  //   const skip = (this.current - 1) * this.limit;
  //   const query = {
  //     limit: this.limit,
  //     skip: skip,
  //     where: {
  //       ...this.where,
  //       ...this.search,
  //       ...this.filter,
  //       ...this.filterDate,
  //       ...this.filterType,
  //     },
  //     include: this.include,
  //   };
  //   if (this.sort) {
  //     query.sort = this.sort;
  //   }
  //   const keys = this.keys || this.view.getKeys() || [];
  //   if (keys.length > 0) {
  //     query.keys = keys;
  //   }
  //   return query;
  // }

  createQuery({ skip = 0, limit = this.limit } = {}) {
    const storeCodes =
      this.store?.flatMap((s) => s.storeAnalytic?.map((a) => a?.name)) ?? [];

    const escapedSearch = this.searchAny
      ?.trim()
      ?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const whereClause = {
      ...(this.where || {}),
      ...(this.filter || {}),
      ...(this.filterDate || {}),
    };

    if (this.filterStore === "AllStore") {
      if (storeCodes.length > 0) {
        whereClause.store_code = { $in: storeCodes };
      }
    } else if (this.filterStore) {
      whereClause.store_code = this.filterStore;
    }

    // 3) Build final query
    const query = {
      limit,
      skip,
      where: whereClause,
      include: this.include,
      keys: this.keys ?? [
        "_id",
        "store_code",
        "tran_date",
        "total",
        "total_taxfree",
        "total_tax",
        "discount_amount",
        "createdAt",
        "cust_name",
        "cust_phone",
        "tran_type",
        "paymenttype",
        "cust_count",
        "total_pretax",
        "status",
      ],
      sort: this.sort,
    };

    return query;
  }

  async findObjects() {
    // 1) Load user + store (kailangan bago gumawa ng whereClause)
    try {
      const user = await getCurrentUserUseCase().execute();
      const storeQuery = { include: ["all"], where: { email: user.email } };
      this.store = await this.findObjectUseCase.execute("users", storeQuery);

      const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
        a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
      );
      this.view.setStore(store);
    } catch (error) {
      console.error("Error fetching user or store data:", error);
      return;
    }

    // 2) Build base filters (AllStore -> $in ng lahat ng analytic names)
    const storeCodes =
      this.store?.[0]?.storeAnalytic?.map((a) => a?.name).filter(Boolean) ?? [];

    const whereClause = {
      ...this.filterDate,
      store_code:
        this.filterStore === "AllStore"
          ? { $in: storeCodes }
          : this.filterStore,
    };

    // fixed: true batching size
    const pageSize = this.limit || 50000;

    // 3) Count muna gamit ang full filter
    let totalCount = 0;
    try {
      this.showProgress();
      this.findObjectUseCase.abort(); // cancel any in-flight

      const { count } = await this.countObjectUseCase.execute(
        this.view.getCollectionName(),
        { where: whereClause },
      );

      totalCount = count ?? 0;
      this.count = totalCount;
      this.view.setCount(totalCount);

      // Reset UI state
      this.objects = [];
      this.view.setObjects([]);
      this.view.setTotal(0);
      this.view.setNotFound(false);

      // Not-found delay (optional)
      if (totalCount === 0) {
        setTimeout(() => {
          if (this.objects.length === 0) {
            console.log("No data after delay. Setting NotFound = true");
            // this.view.setNotFound(true);
          }
        }, 30000);
        // Walang data — wala nang ipe-fetch na pages
        return;
      }

      // 4) Batch pagination loop
      let fetched = 0;
      let page = 0;

      // Shared buffer + periodic flush (para smooth ang UI)
      let buffer = [];
      const flushBuffer = () => {
        if (buffer.length === 0) return;
        this.objects = this.objects.concat(buffer);
        this.view.setObjects(this.objects);
        this.view.setTotal(this.objects.length);
        buffer = [];
      };
      const flushInterval = setInterval(flushBuffer, 1000);

      // NEW: stable sort + seen set + safety cap
      const sort =
        this.sort && Object.keys(this.sort || {}).length > 0
          ? this.sort
          : { createdAt: 1, _id: 1 }; // deterministic fallback

      const seen = new Set(); // track unique ids to avoid infinite repeats
      const maxPages = Math.ceil(totalCount / pageSize) + 2; // safety margin

      try {
        while (fetched < totalCount) {
          const skip = page * pageSize;

          const query = {
            skip,
            limit: pageSize,
            where: whereClause,
            include: ["all"],
            sort, // stable
            // include _id so we can de-dupe safely
            keys: [
              "_id",
              "store_code",
              "tran_date",
              "total",
              "total_taxfree",
              "total_tax",
              "discount_amount",
              "createdAt",
              "cust_name",
              "tran_type",
              "paymenttype",
              "cust_count",
              "total_pretax",
              "status",
            ],
          };

          // Per-page local counters
          let pageRowCount = 0;
          let uniqueAdded = 0;

          const onData = (objects) => {
            if (!objects?.length) return;
            pageRowCount += objects.length;

            // de-dup per page BEFORE pushing to buffer
            for (const obj of objects) {
              const id = obj._id || obj.id;
              if (id && !seen.has(id)) {
                seen.add(id);
                buffer.push(obj);
                uniqueAdded++;
              }
            }
          };

          // Execute one page (may stream sa loob, pero hanggang `limit` lang)
          await this.findObjectUseCase.execute(
            this.view.getCollectionName(),
            query,
            { timeout: 1200000, onData },
          );

          // Push whatever came in this page
          flushBuffer();

          // --- NEW break conditions (very important) ---
          // 1) No rows at all -> done
          if (pageRowCount === 0) break;

          // 2) This page returned duplicates only -> done (prevents endless repeat)
          if (uniqueAdded === 0) break;

          // 3) Short page -> last page
          if (pageRowCount < pageSize) break;

          fetched += pageRowCount;
          page += 1;

          // 4) Safety cap on pages
          if (page >= maxPages) {
            console.warn("Pagination safety cap hit, stopping loop.");
            break;
          }

          // Yield para hindi ma-block UI thread
          await Promise.resolve();
        }
      } finally {
        clearInterval(flushInterval);
        flushBuffer();
      }

      // // Safeguard
      // if (this.objects.length === 0) {
      //   this.view.setNotFound(true);
      // }
    } catch (error) {
      console.error("Error fetching objects:", error);
      this.view.showError(error);
    } finally {
      this.hideProgress();
      this.progress = false;
    }
  }

  getTranType(tranType) {
    const tranTypeMapping = {
      U: "Delivery",
      D: "Dine In",
      T: "Take Out",
      G: "GrabFood",
      F: "FoodPanda",
      P: "Pick Up",
      B: "Del Time",
      K: "Drive-thru",
      H: "Tablevibe",
    };
    return tranTypeMapping[tranType];
  }

  async aggregateData(object) {
    // const objects = this.objects;
    const objects = object;
    // console.log("objecs agg", objects);
    const aggregatedData = {};

    objects.forEach((obj) => {
      const storeCode = obj.store_code;

      if (!aggregatedData[storeCode]) {
        aggregatedData[storeCode] = {};
        aggregatedData[storeCode].totalNetSales = 0;
        aggregatedData[storeCode].totalGrossSales = 0;
        aggregatedData[storeCode].totalPretax = 0;
        aggregatedData[storeCode].totalTax = 0;
        aggregatedData[storeCode].totalTaxFree = 0;
        aggregatedData[storeCode].totalDiscount = 0;
        aggregatedData[storeCode].totalVoidAmount = 0;
        aggregatedData[storeCode].totalVoidCount = 0;
      }

      if (obj.status === "C") {
        const tranType = this.getTranType(obj.tran_type);

        if (!aggregatedData[storeCode][tranType]) {
          aggregatedData[storeCode][tranType] = { tc: 0, netSale: 0 };
        }

        const grossSale = parseFloat(obj.total) || 0;
        const netSale = grossSale;
        const totalNetSales = grossSale - (parseFloat(obj.total_tax) || 0);
        const totalGrossSales = grossSale;
        const totalPretax = parseFloat(obj.total_pretax) || 0;
        const totalTax = parseFloat(obj.total_tax) || 0;
        const totalTaxFree = parseFloat(obj.total_taxfree) || 0;
        const totalDiscount = parseFloat(obj.discount_amount) || 0;

        aggregatedData[storeCode][tranType].tc += 1;
        aggregatedData[storeCode][tranType].netSale += netSale;
        aggregatedData[storeCode].totalNetSales += totalNetSales;
        aggregatedData[storeCode].totalGrossSales += totalGrossSales;
        aggregatedData[storeCode].totalPretax += totalPretax;
        aggregatedData[storeCode].totalTax += totalTax;
        aggregatedData[storeCode].totalTaxFree += totalTaxFree;
        aggregatedData[storeCode].totalDiscount += totalDiscount;
      }

      if (obj.status === "V") {
        // ✅ Handle voids
        const voidAmount = parseFloat(obj.total) || 0;
        aggregatedData[storeCode].totalVoidAmount += voidAmount;
        aggregatedData[storeCode].totalVoidCount += 1;
      }
    });

    this.view.setState({ aggregatedData });
  }

  // async getObjects() {
  //   this.reset();
  //   await this.findObjects();
  //   await this.aggregateData();
  // }

  onChangeDate(where) {
    const { tran_date } = where;

    if (tran_date && tran_date.$gte && tran_date.$lte) {
      const startDate = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDate = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59,
      );

      const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
      const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

      this.filterDate = {
        tran_date: {
          $gte: formattedStartDate,
          $lte: formattedEndDate,
        },
      };
    }
    // this.getObjects();
  }
  onChangeStore(where) {
    this.filterStore = where;
    this.getObjects();
  }

  onChangeType(where) {
    this.filterType = { tran_type: where };
    this.getObjects();
  }
}

export default ReportPresenter;
